import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, ADMIN_TOKEN_KEY, CUSTOMER_TOKEN_KEY } from "../lib/api";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const CART_KEY = "ocrea_cart";

function mergeCarts(serverItems, localItems) {
  const map = new Map();
  const sig = (i) => `${i.productId}|${i.size || ""}|${JSON.stringify(i.customization || null)}`;
  for (const it of [...(serverItems || []), ...(localItems || [])]) {
    const k = sig(it);
    if (map.has(k)) map.get(k).quantity += it.quantity;
    else map.set(k, { ...it, lineId: it.lineId || crypto.randomUUID() });
  }
  return Array.from(map.values());
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // admin: null=checking, false=anon, obj=admin
  const [customer, setCustomer] = useState(null); // client account
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const cartRef = useRef(cart);
  useEffect(() => { cartRef.current = cart; }, [cart]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) return; // /compte handles the Google callback
    // Hydration: if already logged in, the server cart is the source of truth (REPLACE, never merge).
    api.get("/account/me")
      .then(async (r) => {
        setCustomer(r.data);
        try { const { data } = await api.get("/account/cart"); setCart(data.items || []); } catch { /* keep local */ }
      })
      .catch(() => setCustomer(false));
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  // Persist cart to the account whenever it changes while logged in (debounced)
  useEffect(() => {
    if (!(customer && customer.user_id)) return;
    const t = setTimeout(() => { api.put("/account/cart", { items: cart }).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [cart, customer]);

  // Merge the guest cart into the account cart — ONLY on an explicit login/register/google action.
  const syncMergeCart = useCallback(async () => {
    try {
      const { data } = await api.get("/account/cart");
      const merged = mergeCarts(data.items, cartRef.current);
      setCart(merged);
      api.put("/account/cart", { items: merged }).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setUser(false);
  }, []);

  const customerLogin = useCallback(async (email, password) => {
    const { data } = await api.post("/account/login", { email, password });
    if (data.session_token) localStorage.setItem(CUSTOMER_TOKEN_KEY, data.session_token);
    setCustomer(data);
    await syncMergeCart();
    return data;
  }, [syncMergeCart]);

  const customerRegister = useCallback(async (name, email, password) => {
    const { data } = await api.post("/account/register", { name, email, password });
    if (data.session_token) localStorage.setItem(CUSTOMER_TOKEN_KEY, data.session_token);
    setCustomer(data);
    await syncMergeCart();
    return data;
  }, [syncMergeCart]);

  const completeGoogleLogin = useCallback(async (sessionId) => {
    const { data } = await api.post("/account/google", { session_id: sessionId });
    if (data.session_token) localStorage.setItem(CUSTOMER_TOKEN_KEY, data.session_token);
    setCustomer(data);
    await syncMergeCart();
    return data;
  }, [syncMergeCart]);

  const customerLogout = useCallback(async () => {
    await api.post("/account/logout");
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    setCustomer(false);
    setCart([]); // the account cart stays saved server-side; the local cart is cleared so re-login doesn't re-merge a mirror
  }, []);

  const addToCart = useCallback((item) => {
    setCart((c) => [...c, { ...item, lineId: crypto.randomUUID() }]);
  }, []);

  const removeFromCart = useCallback((lineId) => {
    setCart((c) => c.filter((i) => i.lineId !== lineId));
  }, []);

  const updateQty = useCallback((lineId, quantity) => {
    setCart((c) => c.map((i) => (i.lineId === lineId ? { ...i, quantity: Math.max(1, quantity) } : i)));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  return (
    <AppCtx.Provider value={{
      user, setUser, login, logout,
      customer, setCustomer, customerLogin, customerRegister, completeGoogleLogin, customerLogout,
      cart, addToCart, removeFromCart, updateQty, clearCart, cartCount,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
